import React, { useCallback, useState } from 'react';
import {
	Box,
	Chip,
	IconButton,
	Menu,
	MenuItem,
	FormControlLabel,
	Switch,
	Typography,
    ListItemIcon,
    Divider
} from '@mui/material';
import { CustomizedDataGrid } from '../customized_data_grid';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteConfirmationDialog from './DeleteModal';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import { activateAlertConfig, deactivateAlertConfig, deleteAlertConfig } from 'service/service';
import { exportToXlsx } from 'components/exportUtils';
import { useTheme } from "@mui/material/styles";
import HighlightedText from 'components/GlobalSearch/HighlightText';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export const AlertList = ({ isAllowed, alertConfigs, totalCount, updateAlerts, page, setPage, pageSize, setPageSize ,sortingInfo, setSortingInfo, handleSorting}: any) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [filterData] = useState<any[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedRow, setSelectedRow] = useState<any | null>(null);

    const defaultColumns = ['Rule ID', 'Alert Name', 'Source', 'Program', 'Project', 'Sector', 'Alert Type', 'Status', 'Created By', 'Updated By', 'Created At', 'Updated At', 'Action'];

	const handleClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};
    const handleSortingFun = useCallback((sortingInfo: any) => {
        handleSorting({ sortBy: sortingInfo.sortBy, sortDir: sortingInfo.sortDir });
    }, [updateAlerts]);


    const handleToggleStatus = async() => {
		if (selectedRow) {
			if(selectedRow.isActive){
                await deactivateAlertConfig(selectedRow._id)
            }else{
                await activateAlertConfig(selectedRow._id)
            }
            updateAlerts({page, pageSize})
		}
		handleClose();
	};

    const handleDeleteConfirm = async() => {
        if(selectedRow){
            await deleteAlertConfig(selectedRow._id)
            updateAlerts({page, pageSize})
        }
        setOpenDialog(false);
    };

    const handlePageChange = (data:any, page:any) => {
        setPage(page)
        updateAlerts({page, pageSize})
    }

    const handlePageSizeChange = (event:any) => {
        setPageSize(parseInt(event.target.value))
        updateAlerts({page, pageSize: event.target.value})
    }

    const navigate = useNavigate();

    // Alerts Count Formatter
    const formatAlertsCount = (row: any, navigate: any) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
                cursor: 'pointer'
            }}
            onClick={() => navigate(`/alert-details/${row._id}`)}
            // Right-click will open the same action in a new tab
            onContextMenu={(e) => {
                e.preventDefault();
                window.open(`/alert-details/${row._id}`, '_blank');
            }}
        >
            <Typography
                variant="tableRowFont"
                sx={{
                    color: colors.textLight.secondary,
                    textDecoration: 'underline'
                }}
            >
                {row._id}
            </Typography>

            <OpenInNewIcon
                sx={{
                    ml: 1,
                    fontSize: '18px',
                    color: colors.textLight.dark
                }}
            />
        </Box>
    );

    // Status Formatter
    const formatStatus = (row: any, theme: any) => (
        <Chip
            variant='chipLabel'
            style={{
            minWidth: 64,
            color: row.isActive ? colors.boxColor.pgmGreen.color : colors.boxColor.redTrend.color,
            backgroundColor: row.isActive ? colors.boxColor.greenTrend.bgColor : colors.boxColor.redTrend.bgColor,
            marginTop: 2,
            }}
            label={row.isActive ? "ACTIVE" : "INACTIVE"}
        />
    );

    interface FormatActionProps {
        row: any;
        navigate: any;
        isAllowed: boolean;
        anchorEl: HTMLElement | null;
        handleClick: (event: React.MouseEvent<HTMLElement>, row: any) => void;
        handleClose: () => void;
        handleDeleteClick: () => void;
        handleToggleStatus: () => void;
        selectedRow: any;
        colors: any;
    }

    // Action Formatter
    const formatAction = ({
        row,
        navigate,
        isAllowed,
        anchorEl,
        handleClick,
        handleClose,
        handleDeleteClick,
        handleToggleStatus,
        selectedRow,
        colors,
        }: FormatActionProps) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
                aria-label="edit"
                size="small"
                onClick={() => navigate(`/alertFramework/${row._id}`)}
                onContextMenu={(e) => {
                    e.preventDefault();
                    window.open(`/alertFramework/${row._id}`, "_blank");
                }}
                disabled={!isAllowed}
                sx={{ color: `${colors.table.text} !important` }}
            >
                <EditIcon fontSize="small" sx={{ color: `${colors.table.moreIcon} !important` }} />
            </IconButton>

            <IconButton
                aria-label="more"
                size="small"
                onClick={(event) => handleClick(event, row)}
                sx={{ color: `${colors.table.text} !important` }}
            >
                <MoreVertIcon fontSize="small" sx={{ color: `${colors.table.moreIcon} !important` }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            '& .MuiMenu-list': {
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        },
                    },
                }}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: `${colors.moreOptionDropdown?.background} !important`,
                        color: `${colors.moreOptionDropdown?.menuItemText} !important`,
                        borderRadius: '6px',
                        boxShadow: `
                            -2px 0 6px rgba(0, 0, 0, 0.2),
                            2px 0 6px rgba(0, 0, 0, 0.2),
                            0 4px 8px rgba(0, 0, 0, 0.25)
                        `,
                        minWidth: 160,
                    },
                }}
            >
                <MenuItem
                    disabled={!isAllowed}
                    onClick={handleToggleStatus}
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: `${colors.moreOptionDropdown?.menuItemText} !important`,
                        minHeight: '48px !important',
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 32,
                        }}
                    >
                        <Switch
                            checked={selectedRow?.isActive}
                            size="small"
                            color="default"
                            sx={{
                                pointerEvents: 'none',
                                width: 34,
                                height: 20,
                                padding: 0,

                                '& .MuiSwitch-switchBase': {
                                    padding: '3px',
                                    color: colors.toggle.thumb,

                                    '&.Mui-checked': {
                                        transform: 'translateX(14px)',
                                        color: colors.toggle.thumb,

                                        '& + .MuiSwitch-track': {
                                            backgroundColor: colors.toggle.trackOn,
                                            opacity: 1
                                        }
                                    },

                                    '&.Mui-disabled + .MuiSwitch-track': {
                                        backgroundColor: colors.toggle.disabledTrack,
                                        opacity: 1
                                    }
                                },

                                '& .MuiSwitch-thumb': {
                                    width: 14,
                                    height: 14,
                                    backgroundColor: colors.toggle.thumb,
                                    boxShadow: colors.toggle.thumbShadow
                                },

                                '& .MuiSwitch-track': {
                                    borderRadius: '10px',
                                    backgroundColor: colors.toggle.trackOff,
                                    opacity: 1
                                }
                            }}
                        />
                    </ListItemIcon>

                    <Box component="span">
                        {selectedRow?.isActive ? 'Deactivate' : 'Activate'}
                    </Box>
                </MenuItem>

                <Divider
                    sx={{
                        margin: '0 !important',
                        backgroundColor: `${colors.widget.text.noChange}`,
                    }}
                />

                <MenuItem
                    disabled={!isAllowed}
                    onClick={handleDeleteClick}
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
                        minHeight: '48px !important',
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                            backgroundColor: `${colors.moreOptionDropdown?.menuItemHover} !important`,
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 32,
                        }}
                    >
                        <DeleteOutlineIcon
                            sx={{
                                fontSize: '22px',
                                color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
                            }}
                        />
                    </ListItemIcon>

                    <Box
                        component="span"
                        sx={{
                            fontSize: '14px',
                            color: `${colors.moreOptionDropdown?.deleteMenuItem} !important`,
                        }}
                    >
                        Delete
                    </Box>
                </MenuItem>
            </Menu>
        </Box>
    );

    function getColumns() {
        return [
            { key: "_id", name: "Rule ID", minWidth: 50, width: 225, sortable: false, formatter: ({ row }: any) => formatAlertsCount(row, navigate)},
            { key: "ruleName", name: "Alert Name", width: 200, minWidth: 150, sortable: false },
            { key: "source", name: "Source", minWidth: 95, sortable: false, formatter: ({ row }: any) => row.sourceConfig?.sourceType || '' },
            { key: "program", name: "Program", minWidth: 95, sortable: true, },
            { key: "project", name: "Project", minWidth: 95, sortable: true, },
            { key: "sector", name: "Sector", minWidth: 95, sortable: true, },
            { key: "alertType", name: "Alert Type", minWidth: 95,sortable: false },
            {
                key: "isActive",
                name: "Status",
                minWidth: 50,
                sortable: false,
                formatter: ({ row }: any) => formatStatus(row, theme)
            },
            { key: "createdByUser", name: "Created By", minWidth: 95,sortable: false },
            { key: "updatedByUser", name: "Updated By", minWidth: 95,sortable: false },
            { key: "createdDt", name: "Created At", minWidth: 95},
            { key: "updatedDt", name: "Updated At", minWidth: 95},
            {
                key: "action",
                name: "Action",
                sortable: false,
                formatter: ({ row }: any) =>
                formatAction({
                    row,
                    navigate,
                    isAllowed,
                    anchorEl,
                    handleClick,
                    handleClose,
                    handleDeleteClick: () => {
                        setOpenDialog(true);
                        handleClose();
                    },
                    handleToggleStatus,
                    selectedRow,
                    colors,
                }),
            },
        ];
    }
    
    return (
		<Box sx={{ mt: 3, padding: '20px', borderRadius: '8px' }}>
			<CustomizedDataGrid
                title={<HighlightedText text="All Alerts" />}
				tableColumns={getColumns()}
				tableData={alertConfigs || []}
				tableType="AlertList"
				defaultColumnNames={defaultColumns}
				filterDataOptions={filterData}
				listLength={totalCount || 0}
				page={page}
				pageSize={pageSize}
				paginationData={{totalElements: totalCount, totalPages: Math.ceil(totalCount / pageSize)}}
				onSorting={handleSortingFun}
				currSorting={sortingInfo}
				showExport={true}
				fileName={`AlertConfigs-${moment().format()}.xlsx`}
                exportTableData={() => exportToXlsx(`AlertConfigs-${moment().format()}.xlsx`, alertConfigs, getColumns())}
				handlepageSizeChange={handlePageSizeChange}
				handlePageChange={handlePageChange}
				isDateRangeFilter={false}
				isDisabled={false}
                pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
			/>
			{/* Delete Confirmation Dialog */}
			<DeleteConfirmationDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onConfirm={handleDeleteConfirm}
				identifierName={selectedRow?.ruleName || ""}
				type={''}
			/>
		</Box>
	);
};
